import { Link } from 'react-router-dom'
import type { ThesisAllocation, ThesisHealthScore } from '../../types/thesis'
import { DRIFT_STATUS_COLORS, DRIFT_STATUS_LABELS, HEALTH_STATUS_COLORS, HEALTH_SCORE_COLORS } from '../../types/thesis'
import { InfoTooltip } from '../common/InfoTooltip'
import { TOOLTIPS } from '../../constants/tooltips'

interface AllocationDriftCardProps {
  allocation: ThesisAllocation
  healthScore?: ThesisHealthScore
  onClick?: (allocation: ThesisAllocation) => void
}

export function AllocationDriftCard({ allocation, healthScore, onClick }: AllocationDriftCardProps) {
  const hasTarget = allocation.target_allocation_pct !== null

  // Calculate progress bar width (cap at 100% for display)
  const currentPct = Math.min(allocation.current_allocation_pct, 100)
  const targetPct = hasTarget ? Math.min(allocation.target_allocation_pct!, 100) : null

  // Determine if under-allocated (eligible for deploy action)
  const isUnderAllocated = allocation.drift_pct < -2

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-5 hover:shadow-md transition-shadow ${onClick ? 'cursor-pointer' : ''}`}
      onClick={() => onClick?.(allocation)}
    >
      {/* Header */}
      <div className="flex justify-between items-start mb-3">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{allocation.thesis_name}</h3>
          <div className="flex items-center gap-2 mt-1">
            <span className="text-sm text-gray-500">
              {allocation.holdings_count} holding{allocation.holdings_count !== 1 ? 's' : ''}
            </span>
            {allocation.symbols.length > 0 && (
              <>
                <span className="text-gray-300">|</span>
                <span className="text-sm text-gray-500 truncate max-w-[200px]">
                  {allocation.symbols.slice(0, 3).join(', ')}
                  {allocation.symbols.length > 3 && ` +${allocation.symbols.length - 3}`}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          {/* Health Score Badge */}
          {healthScore && (
            <div className="flex items-center gap-1.5">
              <span className={`text-lg font-bold ${HEALTH_SCORE_COLORS[healthScore.status]}`}>
                {healthScore.health_score.toFixed(0)}
              </span>
              <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${HEALTH_STATUS_COLORS[healthScore.status]}`}>
                {healthScore.status}
              </span>
              <InfoTooltip content={TOOLTIPS.healthScore} />
            </div>
          )}
          {/* Drift Status Badge + Deploy Button */}
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${DRIFT_STATUS_COLORS[allocation.drift_status]}`}
            >
              {DRIFT_STATUS_LABELS[allocation.drift_status]}
            </span>
            {isUnderAllocated && (
              <Link
                to={`/strategy/deploy?thesis_id=${allocation.thesis_id}`}
                className="px-2 py-1 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded transition-colors"
                onClick={(e) => e.stopPropagation()}
              >
                Deploy
              </Link>
            )}
          </div>
        </div>
      </div>

      {/* Allocation Progress Bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-gray-600">Current: {allocation.current_allocation_pct.toFixed(1)}%</span>
          {hasTarget && (
            <span className="text-gray-500">Target: {allocation.target_allocation_pct!.toFixed(1)}%</span>
          )}
        </div>
        <div className="h-3 bg-gray-100 rounded-full overflow-hidden relative">
          {/* Target indicator line */}
          {hasTarget && targetPct !== null && (
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-gray-400 z-10"
              style={{ left: `${targetPct}%` }}
            />
          )}
          {/* Current allocation bar */}
          <div
            className={`h-full transition-all duration-300 ${
              allocation.drift_status === 'ok'
                ? 'bg-green-500'
                : allocation.drift_status === 'warning'
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
            }`}
            style={{ width: `${currentPct}%` }}
          />
        </div>
      </div>

      {/* Drift Info */}
      {hasTarget && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-600">Drift:</span>
          <span
            className={`text-sm font-medium ${
              allocation.drift_pct > 0
                ? 'text-orange-600'
                : allocation.drift_pct < 0
                  ? 'text-blue-600'
                  : 'text-gray-600'
            }`}
          >
            {allocation.drift_pct > 0 ? '+' : ''}
            {allocation.drift_pct.toFixed(1)}%
          </span>
          <span className="text-sm text-gray-400">
            ({allocation.drift_pct > 0 ? 'over-allocated' : allocation.drift_pct < 0 ? 'under-allocated' : 'on target'})
          </span>
        </div>
      )}

      {/* Value Info */}
      <div className="flex items-center justify-between text-sm pt-3 border-t border-gray-100">
        <div>
          <span className="text-gray-500">Market Value</span>
          <p className="font-semibold text-gray-900">{formatCurrency(allocation.total_market_value)}</p>
        </div>
        <div className="text-right">
          <span className="text-gray-500">Cost Basis</span>
          <p className="font-semibold text-gray-900">{formatCurrency(allocation.total_cost_basis)}</p>
        </div>
        <div className="text-right">
          <span className="text-gray-500">Gain/Loss</span>
          <p
            className={`font-semibold ${
              allocation.total_market_value >= allocation.total_cost_basis
                ? 'text-green-600'
                : 'text-red-600'
            }`}
          >
            {formatCurrency(allocation.total_market_value - allocation.total_cost_basis)}
          </p>
        </div>
      </div>
    </div>
  )
}
